import type {
  CheckoutAvailabilityState,
  CheckoutDeliveryTextColor,
  CheckoutOrderableProduct,
  CheckoutPowerEfficiencyFlag,
  CheckoutPriceCurrency,
  CheckoutTireEfficiencyFlag,
  LineItemFragmentFragment,
} from '@packages/gql/generated/shopping/graphql';
import { toDisplayMessages, type DisplayMessage } from '../../utils/messageSeverity';

type Maybe<T> = T | null;

export type CheckoutAplusService = {
  uuid: string;
  name: string | null;
  serviceIcon: string;
  included: boolean | null;
  selected: boolean | null;
  serviceCount: { current: number; maximum: number | null } | null;
  price: { value: number; currency: CheckoutPriceCurrency };
};

export type CheckoutAplusCluster = {
  label: string;
  services: CheckoutAplusService[];
};

export type LineItemNormalizedPowerEfficiency = {
  level: string;
  text: string;
  link: string;
  dataSheet?: string | null;
};

export type LineItemNormalizedTireEfficiency = {
  type: 'FuelEfficiency' | 'WetGrip' | 'ExternalRollingNoise';
  level: string;
  link: string;
  dataSheet?: string | null;
};

export type LineItemNormalizedPrice = {
  currency: string;
  value: number;
  valueOld?: number | null;
  uvp?: boolean | null;
  saving?: { type: string; value: number } | null;
  unit?: string | null;
  valueUnit?: number | null;
  amount?: number | null;
  isStartPrice?: boolean | null;
};

export type LineItemNormalizedSinglePrice = {
  amount: number;
  currency: string;
};

export type LineItemNormalizedAvailability = {
  message: string;
  state: string;
  limitedStock?: boolean;
};

export type LineItemDisplayProps = {
  id?: string | null;
  name: string;
  sku: string;
  akl?: string | null;
  orderNumber: string;
  imageUrl: string;
  variation?: string | null;
  sustained?: boolean | null;
  marketingFlag?: { text: string } | null;
  additionalFlags: Array<{ text: string; type?: string | null; src?: string | null }>;
  configuration?: {
    length?: number | null;
    width?: number | null;
    engraving?: string | null;
    serialNumber?: string | null;
  } | null;
  powerEfficiency?: LineItemNormalizedPowerEfficiency[] | null;
  tireEfficiency?: LineItemNormalizedTireEfficiency[] | null;
  powerSupply?: {
    chargerType: string;
    maxPower: number;
    minPower: number;
    powerSupplyIncluded: string;
  } | null;
  price: LineItemNormalizedPrice;
  singlePrice?: LineItemNormalizedSinglePrice | null;
  isSoldOut: boolean;
  availability: LineItemNormalizedAvailability;
  quantity: number;
  productMessages?: DisplayMessage[];
  actionsSlot?: React.ReactNode;
  aplusSlot?: React.ReactNode;
};

/** Display props passed directly to LineItemContent — without slot/layout props */
export type LineItemContentDisplayProps = Omit<LineItemDisplayProps, 'actionsSlot' | 'aplusSlot'>;

const deliveryTextColorToAvailabilityState = (color: CheckoutDeliveryTextColor): string => {
  const map: Record<CheckoutDeliveryTextColor, CheckoutAvailabilityState> = {
    success: 'AVAILABLE',
    warning: 'NOT_YET_AVAILABLE',
    error: 'NOT_AVAILABLE',
  };
  return map[color] ?? 'AVAILABLE';
};

export const basketLineItemToDisplayProps = (
  data: LineItemFragmentFragment,
): LineItemContentDisplayProps => ({
  id: data.id,
  name: data.product.name,
  sku: data.product.sku,
  akl: data.product.akl,
  orderNumber: data.product.orderNumber,
  imageUrl: data.product.imageUrl,
  variation: data.product.variation,
  sustained: data.product.sustained,
  marketingFlag: data.product.marketingFlag ? { text: data.product.marketingFlag.text } : null,
  additionalFlags: data.product.additionalFlags ?? [],
  configuration: data.configuration ?? null,
  powerEfficiency: data.product.powerEfficiencyFlags ?? null,
  tireEfficiency: data.product.tireEfficiencyFlags ?? null,
  powerSupply: data.product.powerSupply ?? null,
  price: {
    currency: data.price.currency,
    value: data.price.value,
    valueOld: data.price.valueOld,
    uvp: data.price.uvp,
    saving: data.price.saving ?? null,
    unit: data.price.unit,
    valueUnit: data.price.valueUnit,
    amount: data.price.amount,
    isStartPrice: data.price.isStartPrice,
  },
  singlePrice: data.singlePrice
    ? { amount: data.singlePrice.amount, currency: data.singlePrice.currency }
    : null,
  isSoldOut: data.isSoldOut,
  availability: {
    message: data.availability.message,
    state: data.availability.state,
    limitedStock: data.availability.limitedStock,
  },
  quantity: data.quantity,
});

/**
 * Subset of CheckoutOrderableProduct fields returned by the GetPreparedBasket query.
 * Uses Pick for pass-through fields; overrides for fields where only a subset is queried.
 */
export type CheckoutLineItemProduct = Pick<
  CheckoutOrderableProduct,
  | 'lineItemId'
  | 'name'
  | 'sku'
  | 'akl'
  | 'orderNumber'
  | 'imageUrl'
  | 'variation'
  | 'sustained'
  | 'additionalFlags'
  | 'configuration'
  | 'marketingFlag'
  | 'powerSupply'
  | 'price'
  | 'singlePrice'
  | 'isSoldOut'
  | 'limitedStock'
  | 'deliveryMessage'
  | 'deliveryTextColor'
  | 'quantity'
  | 'messages'
> & {
  aplus?: CheckoutAplusCluster[] | null;
  // Only a subset of fields is queried (no `iconText`, `icon`), so we override with a narrower Pick
  powerEfficiency?: Maybe<
    Array<
      Pick<
        CheckoutPowerEfficiencyFlag,
        'text' | 'level' | 'link' | 'dataSheet' | 'color' | 'hasOldLabel'
      >
    >
  >;
  tireEfficiency?: Maybe<
    Array<Pick<CheckoutTireEfficiencyFlag, 'type' | 'level' | 'link' | 'dataSheet'>>
  >;
};

export const checkoutProductToDisplayProps = (
  product: CheckoutLineItemProduct,
): LineItemContentDisplayProps => ({
  id: product.lineItemId,
  name: product.name,
  sku: product.sku,
  akl: product.akl,
  orderNumber: product.orderNumber,
  imageUrl: product.imageUrl,
  variation: product.variation,
  sustained: product.sustained,
  marketingFlag: product.marketingFlag ? { text: product.marketingFlag.text } : null,
  additionalFlags: product.additionalFlags ?? [],
  configuration: product.configuration ?? null,
  powerEfficiency: product.powerEfficiency ?? null,
  tireEfficiency: product.tireEfficiency ?? null,
  powerSupply: product.powerSupply ?? null,
  price: {
    currency: product.price.currency,
    value: product.price.value,
    valueOld: product.price.valueOld,
    uvp: product.price.uvp,
    saving: product.price.saving ?? null,
    unit: product.price.unit,
    valueUnit: product.price.valueUnit,
    amount: product.price.amount,
    isStartPrice: product.price.isStartPrice,
  },
  singlePrice: product.singlePrice
    ? { amount: product.singlePrice.value, currency: product.singlePrice.currencyCode }
    : null,
  isSoldOut: product.isSoldOut ?? false,
  availability: {
    message: product.deliveryMessage,
    state: deliveryTextColorToAvailabilityState(product.deliveryTextColor),
    limitedStock: product.limitedStock ?? false,
  },
  quantity: product.quantity ?? 1,
  productMessages: toDisplayMessages(product.messages?.elements),
});
